import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import { mapMutations } from "vuex";
import * as types from "@survey/store/types";
import api from "@survey/api";
import SurveyThumbnail from "@survey/components/Common/SurveyThumbnail.vue";

@Component({
    components: {
        SurveyThumbnail,
    },
    methods: {
        ...mapMutations({
            deleteAnswer: types.SURVEY_DELETE_ANSWER,
        }),
    },
})
export default class SurveyReadExamples extends Vue {
    @Prop({ type: Number }) questionId!: number;
    @Prop({ type: Array }) value!: Array<survey.SurveyExampleItem>;
    @Prop({ type: Number }) questionType!: number;
    @Prop({ type: Boolean }) isAdditionalOption!: boolean;
    @Prop({ type: Array }) answers!: Array<survey.SurveyAnswerItem>;
    @Prop({ type: Boolean }) isCompleted!: boolean;
    @Prop({ type: Number }) surveyStatus!: number;
    @Prop({ type: Boolean }) isStatisticsMode!: boolean;

    deleteAnswer!: (payload: any) => void;

    onImageLoading: boolean = false;
    additionalAnswer: string = "";
    textAnswer: string = "";
    imageUrls: { [key: string]: string | undefined } = {};

    @Watch("value")
    onExamplesChanged(newVal: survey.SurveyExampleItem[]) {
        this.mountImageUrls(newVal);
    }

    created() {
        if (this.isCompleted && this.answers.length > 0) {
            if (this.questionType === 2) {
                this.textAnswer = this.answers[0].surveyAnswer.toString();
            }

            var found = this.answers.find((a) => a.isAdditionalAnswer);

            if (found) {
                this.additionalAnswer = found?.surveyAnswer ?? "";
            }
        }

        this.mountImageUrls(this.value);
    }

    mountImageUrls(examples: survey.SurveyExampleItem[]): Promise<appbase.FileInfo[][]> {
        this.onImageLoading = true;
        let imagePromise: Promise<appbase.FileInfo[]>[] = [];

        examples
            .filter((v) => v.fileCode !== "")
            .forEach((v) =>
                imagePromise.push(
                    api.survey.getSurveyAttachmentList(v.fileCode).then((res) => {
                        if (v.surveyExampleId) {
                            this.imageUrls[`img-${v.surveyExampleId}`] = res[0].downloadUrl;
                        }

                        return res;
                    })
                )
            );

        return Promise.all(imagePromise).finally(() => {
            this.onImageLoading = false;
        });
    }

    getChecked(exampleId: number): boolean {
        if (!this.isCompleted) {
            return false;
        } else {
            return this.answers.findIndex((a) => a.surveyExampleId === exampleId) >= 0;
        }
    }

    getTextExampleAnswer(exampleId: number): string {
        return this.answers.find((a) => a.surveyExampleId === exampleId)?.surveyAnswer ?? "";
    }

    getAdditionalAnswerChecked(): boolean {
        if (!this.isCompleted) {
            return false;
        } else {
            return this.answers.findIndex((a) => a.isAdditionalAnswer) >= 0;
        }
    }

    onAnswerChecked(e: Event, exampleId: number | null) {
        let type = (e.target as HTMLInputElement).type;
        let checked = (e.target as HTMLInputElement).checked;

        let updateData: survey.SurveyAnswerItem = {
            questionType: this.questionType,
            surveyId: 0,
            surveyQuestionId: this.questionId,
            surveyExampleId: exampleId,
            surveyAnswer: exampleId?.toString() ?? "",
            isAdditionalAnswer: false,
            isMultipleChoice: true,
        };

        if (type === "checkbox" && !checked) {
            this.deleteAnswer({
                answers: exampleId ?? "",
                questionId: this.questionId,
                isAdditionalAnswer: false,
            });
        } else {
            this.$emit("update", updateData);
        }
    }

    onAdditionalAnswerChecked(e: Event) {
        let type = (e.target as HTMLInputElement).type;
        let checked = (e.target as HTMLInputElement).checked;

        let updateData: survey.SurveyAnswerItem = {
            questionType: this.questionType,
            surveyId: 0,
            surveyQuestionId: this.questionId,
            surveyExampleId: null,
            surveyAnswer: this.additionalAnswer,
            isAdditionalAnswer: true,
            isMultipleChoice: false,
        };

        if (type === "checkbox" && !checked) {
            this.deleteAnswer({
                answers: this.additionalAnswer,
                questionId: this.questionId,
                isAdditionalAnswer: true,
            });
        } else {
            this.$emit("update", updateData);
        }
    }

    onAdditionalAnswerChanged(e: Event) {
        let updateData: survey.SurveyAnswerItem = {
            questionType: this.questionType,
            surveyId: 0,
            surveyQuestionId: this.questionId,
            surveyExampleId: null,
            surveyAnswer: this.additionalAnswer,
            isAdditionalAnswer: true,
            isMultipleChoice: false,
        };

        this.$emit("update", updateData);
    }

    onTextAnswerChanged(e: Event) {
        let answer = (e.target as HTMLTextAreaElement).value;

        let updateData: survey.SurveyAnswerItem = {
            questionType: this.questionType,
            surveyId: 0,
            surveyQuestionId: this.questionId,
            surveyExampleId: null,
            surveyAnswer: answer,
            isAdditionalAnswer: false,
            isMultipleChoice: false,
        };

        this.$emit("update", updateData);
    }

    onExampleAnswerChanged(e: Event, exampleId: number | null) {
        let answer = (e.target as HTMLInputElement).value;

        let updateData: survey.SurveyAnswerItem = {
            questionType: this.questionType,
            surveyId: 0,
            surveyQuestionId: this.questionId,
            surveyExampleId: exampleId,
            surveyAnswer: answer,
            isAdditionalAnswer: false,
            isMultipleChoice: true,
        };

        this.$emit("update", updateData);
    }

    getDownloadUrl(exampleId?: number): string | undefined {
        return this.imageUrls[`img-${exampleId}`];
    }
}
